import React from 'react';

import { hockeyFormatter, statFormatter, stringFormatter } from '@nhl/formatter-utilities';

import { FranchisePages } from '../../../redux/ducks/franchises/viewTypes';
import { ColumnDefinition } from '../../../types/ReactTableTypes';
import GlobalColumnFunctions from '../../../utils/globalColumnFunctions';
import { ExpanderIcon, TeamDisplayModule } from '../../components';

const sumCount = (data) => data.reduce((acc, item = 0) => acc + item, 0);

const createColumnDefinition = (header: string, accessor: string): ColumnDefinition => ({
    Header: header,
    accessor,
    aggregate: (vals) => sumCount(vals),
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    sortDescFirst: true,
    minWidth: 60,
    maxWidth: 130,
    getProps: GlobalColumnFunctions.getProps,
});

const hasBeenInSeries = (standing, value, modifier = '--') =>
    standing.original.seriesPlayed > 0 ? standing.original[value] : modifier;

export const createPlayoffColumnDefinition = (columnProps) => {
    return {
        ...columnProps,
        Cell: ({ row }) => hasBeenInSeries(row, columnProps.accessor),
    };
};

const GAMES_PLAYED_COL = createColumnDefinition('Games', 'gamesPlayed');
const OVERTIME_GAMES_PLAYED_COL = createColumnDefinition('OT Games', 'overtimeGamesPlayed');
const WINS_COL = createColumnDefinition('Wins', 'wins');
const OVERTIME_WINS_COL = createColumnDefinition('OT Wins', 'overtimeWins');
const LOSSES_COL = createColumnDefinition('Losses', 'losses');
const TIES_COL = createColumnDefinition('Ties', 'ties');
const OVERTIME_TIES_COL = createColumnDefinition('OT Ties', 'overtimeTies');
const OVERTIME_LOSSES_COL = (defunct) => ({
    ...createColumnDefinition('OT/SO Losses', 'overtimeLosses'),
    show: !defunct,
});

const GOALS_FOR_COL = createColumnDefinition('Goals For', 'goalsFor');
const GOALS_AGAINST_COL = createColumnDefinition('Goals Against', 'goalsAgainst');
const POINTS_COL = createColumnDefinition('Points', 'points');

const CUPS_COL = createColumnDefinition('Cups', 'cups');
const PLAYOFF_SEASONS_COL = createColumnDefinition('Playoff Seasons', 'playoffSeasons');
const SERIES_COL = createColumnDefinition('Series', 'seriesPlayed');
const SERIES_WINS_COL = createColumnDefinition('Series Wins', 'seriesWins');
const SERIES_LOSSES_COL = createColumnDefinition('Series Losses', 'seriesLosses');

const SERIES_WIN_PCTG_COL = {
    Header: 'Series Win %',
    accessor: 'seriesWinPctg',
    aggregate: (vals, rows) => {
        // This is taking the rows totals, summing them and applying the pointPctg formula.
        const seriesWins = sumCount(rows.map((row) => row.seriesWins));
        const seriesPlayed = sumCount(rows.map((row) => row.seriesPlayed));
        return seriesWins / seriesPlayed;
    },
    Cell: ({ value }) => {
        // This is the value of the cell of the expanded row(s). Not the main pivot row.
        return statFormatter.displayValueAs('winPctg')(value);
    },
    sortDescFirst: true,
    minWidth: 60,
    maxWidth: 130,
    getProps: GlobalColumnFunctions.getProps,
    sortType: 'basic',
};

const FRANCHISE_ID_COL = (firstSeasonColumn) => ({
    Header: 'Franchise',
    id: 'franchiseId',
    headerClassName: 'left',
    className: 'left',
    minWidth: 280,
    accessor: 'franchiseSlug',
    Cell: (cellData) => {
        const { canExpand, isExpanded, subRows } = cellData.row;
        if (canExpand) {
            const team = subRows
                .map((r) => r.original)
                .reduce(
                    (acc, val) => (val[firstSeasonColumn] > acc[firstSeasonColumn] ? val : acc),
                    subRows[0].original
                );
            const hasMultipleSubRows = subRows.length > 1;
            const toggleProps = hasMultipleSubRows ? cellData.row.getToggleRowExpandedProps() : {};
            return (
                <div className={isExpanded ? 'pivot-expanded' : 'pivot'} {...toggleProps}>
                    <TeamDisplayModule
                        team={team}
                        useInTable
                        franchisePage={isExpanded ? false : FranchisePages.SEASON_BY_SEASON_RECORD}
                        append={isExpanded ? <div className="totals">(Totals)</div> : ''}
                        displayNameOverride={team.fullName}
                    />
                    {hasMultipleSubRows && ExpanderIcon(isExpanded)}
                </div>
            );
        }
        const team = cellData.original;
        return (
            <div style={{ marginLeft: '35px' }}>
                <TeamDisplayModule team={team} franchisePage={FranchisePages.SEASON_BY_SEASON_RECORD} useInTable />
            </div>
        );
    },
    getProps: GlobalColumnFunctions.getProps,
});

const OT_FRANCHISE_ID_COL = {
    Header: 'Franchise',
    id: 'franchiseId',
    headerClassName: 'left',
    className: 'left',
    minWidth: 280,
    accessor: 'fullName',
    Cell: (cellData) => {
        const team = cellData.original;
        return <TeamDisplayModule team={team} useInTable franchisePage={FranchisePages.SEASON_BY_SEASON_RECORD} />;
    },
};

const POINT_PCTG_COL = {
    Header: 'Point %',
    accessor: 'pointPctg',
    aggregate: (vals, rows) => {
        // This is taking the rows totals, summing them and applying the pointPctg formula.
        const points = sumCount(rows.map((row) => row.points));
        const gamesPlayed = sumCount(rows.map((row) => row.gamesPlayed));
        return points / (gamesPlayed * 2);
    },
    Cell: ({ value }) => {
        // This is the value of the cell of the expanded row(s). Not the main pivot row.
        return statFormatter.displayValueAs('pointPctg')(value);
    },
    sortDescFirst: true,
    minWidth: 60,
    maxWidth: 130,
    getProps: GlobalColumnFunctions.getProps,
    sortType: 'basic',
};

const WIN_PCTG_COL = {
    Header: 'Win %',
    accessor: 'winPctg',
    aggregate: (vals, rows) => {
        // This is taking the rows totals, summing them and applying the pointPctg formula.
        const wins = sumCount(rows.map((row) => row.wins));
        const gamesPlayed = sumCount(rows.map((row) => row.gamesPlayed));
        return wins / gamesPlayed;
    },
    Cell: ({ value }) => statFormatter.displayValueAs('winPctg')(value),
    sortDescFirst: true,
    minWidth: 60,
    maxWidth: 130,
    getProps: GlobalColumnFunctions.getProps,
    sortType: 'basic',
};

const FIRST_SEASON_COL = {
    Header: 'First Season',
    accessor: 'firstSeasonId',
    aggregate: (vals) => Math.min(...vals),
    Cell: ({ row }) => hockeyFormatter.singleSeasonFormatter(row.original.firstSeasonId),
    disableSortBy: true,
    getProps: GlobalColumnFunctions.getProps,
};

const LAST_SEASON_COL = (defunct) => ({
    Header: 'Last Season',
    accessor: 'lastSeasonId',
    aggregate: (vals) => Math.max(...vals),
    Cell: ({ row }) => hockeyFormatter.singleSeasonFormatter(row.original.lastSeasonId),
    disableSortBy: true,
    show: defunct,
    getProps: GlobalColumnFunctions.getProps,
});

const HOME_COLS = {
    Header: 'Home',
    headerClassName: 'top-level-header',
    columns: [
        { ...GAMES_PLAYED_COL, accessor: 'homeGamesPlayed', headerClassName: 'header-group-start' },
        { ...WINS_COL, accessor: 'homeWins' },
        { ...LOSSES_COL, accessor: 'homeLosses' },
        { ...TIES_COL, accessor: 'homeTies' },
        { ...WIN_PCTG_COL, accessor: 'homeWinPctg', headerClassName: 'header-group-end' },
    ],
};
const ROAD_COLS = {
    Header: 'Road',
    headerClassName: 'top-level-header',
    columns: [
        { ...GAMES_PLAYED_COL, accessor: 'roadGamesPlayed', headerClassName: 'header-group-start' },
        { ...WINS_COL, accessor: 'roadWins' },
        { ...LOSSES_COL, accessor: 'roadLosses' },
        { ...TIES_COL, accessor: 'roadTies' },
        { ...WIN_PCTG_COL, accessor: 'roadWinPctg', headerClassName: 'header-group-end' },
    ],
};
const TOTAL_COLS = {
    Header: 'Total',
    headerClassName: 'top-level-header',
    columns: [
        { ...GAMES_PLAYED_COL, headerClassName: 'header-group-start' },
        { ...WINS_COL },
        { ...LOSSES_COL },
        { ...TIES_COL },
        { ...WIN_PCTG_COL, headerClassName: 'header-group-end' },
    ],
};

export default {
    GAMES_PLAYED_COL,
    WINS_COL,
    LOSSES_COL,
    TIES_COL,
    OVERTIME_GAMES_PLAYED_COL,
    OVERTIME_LOSSES_COL,
    OVERTIME_WINS_COL,
    OVERTIME_TIES_COL,
    GOALS_FOR_COL,
    GOALS_AGAINST_COL,
    POINTS_COL,
    CUPS_COL,
    PLAYOFF_SEASONS_COL,
    SERIES_COL,
    SERIES_WINS_COL,
    SERIES_LOSSES_COL,
    SERIES_WIN_PCTG_COL,
    FRANCHISE_ID_COL,
    POINT_PCTG_COL,
    WIN_PCTG_COL,
    FIRST_SEASON_COL,
    LAST_SEASON_COL,
    OT_FRANCHISE_ID_COL,
    HOME_COLS,
    ROAD_COLS,
    TOTAL_COLS,
};
